import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    QueryList,
    SimpleChanges,
    ViewChildren
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

import * as moment from 'moment';
import _toString from 'lodash-es/toString';
import _upperFirst from 'lodash-es/upperFirst';
import _find from 'lodash-es/find';
import _set from 'lodash-es/set';

import { wenji } from "libraries/interfaces/IWenji";
import { GenericFieldset, TConfig } from '../generic-fieldset.component';
import { FormBuilderService } from "libraries/service/formBuilder/formBuilder.service";
import { Yii } from "libraries/interfaces/Yii";
import { IDateFieldSpecs, IFieldSpecs } from "../../fields/interfaces/field-specs";
import { IValidator } from "../../forms/utils/validators/IValidator";
import { IsPast } from "../../forms/utils/validators/isPast.validator";
import { WORDING } from './config/wording';
import { Required } from '../../forms/utils/validators/required.validator';
import { RequiredIf } from '../../forms/utils/validators/requiredIf.validator';
import { IAgesConfig } from '../../forms/travellerForm/travellerForm.component';
import { ILabels } from 'libraries/interfaces/ILabels';
import { IAgeLimitDefault } from './age-limit-config.interface';
import { IsCorrectDateForType } from './is-correct-date-for-type.validator';
import { debounceTime } from 'rxjs/operators';
import { RuntimeError } from "libraries/service/error-tracing/runtime-error.model";
import { IdentityInfoEditorComponent } from "libraries/component/identity-info-editor/identity-info-editor.component";
import { ITravellerObject } from 'libraries/service/resources/hotel/ISearchParameters';
import { IIdentityInfo } from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { TravellerFieldsetService } from './services/traveller-fieldset.service';

import { faCaretRight, faCaretDown, IconDefinition } from "@fortawesome/free-solid-svg-icons";

interface IMinMaxBirthDate {
    Adult: Date;
    Child: Date;
    Infant: Date;
}

interface IDateRange {
    Adult: string;
    Child: string;
    Infant: string;
}

const PASSPORT_FIELDS: string[] = ["passportCitizenship", "passport.passNumber", "passport.issuance.passCountry", "passport.validity.passEnd"];
const IDENTITY_CARD_FIELDS: string[] = ["identityCardCitizenship", "identityCard.cardNumber", "identityCard.issuance.cardCountry", "identityCard.validity.cardEnd"];
const SFPD_FIELDS: string[] = ["xGender", "secureFlightPassengerData.birthDate"];

/**
 * Traveller-fieldset
 * Generate a fieldset of needed fields
 */
@Component({
    selector: 'traveller-fieldset',
    templateUrl: './traveller-fieldset.component.html',
    styleUrls: ['./traveller-fieldset.component.scss'],
})
export class TravellerFieldsetComponent extends GenericFieldset implements OnInit, OnDestroy {

    @ViewChildren('identityInfoEditorComponent')
    public identityInfoEditorComponents: QueryList<IdentityInfoEditorComponent>;

    @Input()
    public validators: {
        [string: string]: IValidator
    };
    @Input()
    public apiServiceQualityMessage: string[];


    public travellerCollapse?: { link: number; open: number } = wenji.ui.travellerCollapse;

    /** emitter */
    public eData: EventEmitter<string> = new EventEmitter();
    /** to replicate the fieldset */
    /** Group as to be FormArray */
    @Input()
    public group: FormArray;
    /** Group as to be FormArray */
    @Input()
    public columns: boolean = false;

    /** to replicate the fieldset */
    @Input()
    public nbItems: number;
    // /** to replicate the fieldset */

    @Input()
    public travellerItem: ITravellerObject[];

    @Input()
    public disableTypes: boolean;

    /** Config */
    @Input()
    public config: TConfig;

    @Input()
    public ageLimitConfig: IAgesConfig;

    @Input()
    public apisForm: boolean = true;

    @Input()
    public contextIsModifySegment: boolean = false;

    /** Readonly context can't be modified. Define the context of the fieldset */
    public readonly context: string = 'traveller';

    public openApis: any = {};
    public initialPassport: IIdentityInfo[] = [];
    public initialIdentityCard: IIdentityInfo[] = [];
    public isTravellerReadonly: boolean[] = [];

    public options = [
        { label: Yii.t('wui-shared-js', 'M'), value: 'Mr' },
        { label: Yii.t('wui-shared-js', 'Mme'), value: 'Ms' },
    ];

    public optionsType = [
        { label: Yii.t('wui-shared-js', 'Adulte'), value: 'Adult' },
        { label: Yii.t('wui-shared-js', 'Enfant'), value: 'Child' },
        { label: Yii.t('wui-shared-js', 'Bébé'), value: 'Infant' },
    ];

    public minBirthDate: IMinMaxBirthDate = {
        Adult: null,
        Child: null,
        Infant: null
    };
    public maxBirthDate: IMinMaxBirthDate = {
        Adult: null,
        Child: null,
        Infant: null,
    };
    public defaultBirthDate: IMinMaxBirthDate = {
        Adult: null,
        Child: null,
        Infant: null,
    };
    public yearRange: IDateRange = {
        Adult: null,
        Child: null,
        Infant: null,
    };


    public wording: ILabels = {
        traveller: Yii.t('wui-shared-js', 'Voyageur'),
        title: Yii.t('wui-shared-js', 'Titre'),
        type: Yii.t('wui-shared-js', 'Type'),
        apisInput: Yii.t('wui-shared-js', 'Saisie des Apis'),
        passportNumber: Yii.t('wui-shared-js', 'N. de passeport'),
    };

    public fieldsConfig: Array<{ [key: string]: IFieldSpecs | IDateFieldSpecs }>;

    public iconCaretRight: IconDefinition = faCaretRight;
    public iconCaretDown: IconDefinition = faCaretDown;

    private readonly subscription: Subscription = new Subscription();

    /** used to stock nb of Travellers */
    private nbFields: number;

    protected ageLimitDefaults: IAgeLimitDefault = {
        Adult: {
            min: moment().subtract(150, 'years').toDate(),
            max: moment().subtract(10, 'years').toDate()
        },
        Child: {
            min: moment().subtract(10, 'years').toDate(),
            max: moment().subtract(2, 'years').toDate()
        },
        Infant: {
            min: moment().subtract(2, 'years').toDate(),
            max: new Date()
        },
    };

    /**
     * test value of first Last Name, and each last Name values
     * launched on focus on first pax lastname
     * @param _event
     */
    private sameAndEmptyLastNameTraveller: any = [];

    /** DI - Formbuilder */
    constructor(
        public fb: FormBuilder,
        public formBuilderService: FormBuilderService,
        public readonly travellerFieldsetService: TravellerFieldsetService,
    ) {
        super(fb);
    }

    public ngOnInit(): void {
        this.initWording();

        //set yearRanges for each types
        const todayYear = moment().format('YYYY');
        const past2Year = moment().subtract(2, 'years').format('YYYY');
        const past10Year = moment().subtract(10, 'years').format('YYYY');
        const past150Year = moment().subtract(150, 'years').format('YYYY');

        //set values for Adult
        _set(this.yearRange, 'Adult', past150Year + ":" + past10Year);
        _set(this.minBirthDate, 'Adult', moment().subtract(150, 'years').toDate());
        _set(this.maxBirthDate, 'Adult', moment().subtract(50, 'years').toDate());
        _set(this.defaultBirthDate, 'Adult', this.maxBirthDate.Adult);

        //set values for Child
        _set(this.yearRange, 'Child', past10Year + ":" + past2Year);
        _set(this.minBirthDate, 'Child', moment().subtract(150, 'years').toDate());
        _set(this.maxBirthDate, 'Child', moment().subtract(5, 'years').toDate());
        _set(this.defaultBirthDate, 'Child', this.maxBirthDate.Child);

        //set values for Infant
        _set(this.yearRange, 'Infant', past2Year + ":" + todayYear);
        _set(this.minBirthDate, 'Infant', moment().subtract(150, 'years').toDate());
        _set(this.maxBirthDate, 'Infant', moment().subtract(1, 'year').toDate());
        _set(this.defaultBirthDate, 'Infant', this.maxBirthDate.Infant);

        this.initFields();
        this.initAgeLimitDefaults();
        this.updateDateConfig();
        this.updateNameConfig();
        this.initIdentityInfo();

        this.subscription.add(
            this.formBuilderService
                .info
                .subscribe(
                    (data: any) => {
                        if (data.quantity) {
                            const qtt: number = data.quantity.adult + data.quantity.child + data.quantity.infant;
                            this.nbFields = this.group.controls.length;

                            if (qtt > this.nbFields) {
                                this.addNbFields(qtt - this.nbFields, { path: 'id', value: 'T_' }, true);
                            }
                            if (qtt < this.nbFields) {
                                for (let i:number = this.nbFields; i >= qtt; i--) {
                                    this.removeField(i);
                                }
                            }
                            this.nbFields = qtt;

                            this.initFields();
                            this.updateDateConfig();
                            this.updateBirthDateConfig();
                            this.updateNameConfig();
                        }
                    }
                )
        );

        this.subscription.add(
            this.group.valueChanges
                .pipe(
                    debounceTime(500)
                )
                .subscribe({
                    next: (): void => {
                        this.emitData();
                    }
                })
        );

        for (let i: number = 0; i < this.group.length; i++) {
            this.subscription.add(
                this.group.get([i, 'type'])
                    .valueChanges
                    .subscribe(() => {
                        this.group.get([i, 'birthDate']).updateValueAndValidity();
                    })
            );
        }

        this.updateBirthDateConfig();
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private initWording(): void {
        if (this.contextIsModifySegment) {
            this.setWording('apisInput', Yii.t('wui-shared-js', 'Voir des Apis'));
        }
    }

    public initAgeLimitDefaults(): void {
        const defaultAcc: IAgeLimitDefault = {
            Adult: {
                min: moment().subtract(150, 'years').toDate(),
                max: new Date()
            },
            Child: {
                min: moment().subtract(150, 'years').toDate(),
                max: new Date()
            },
            Infant: {
                min: moment().subtract(150, 'years').toDate(),
                max: new Date()
            }
        };
        const parseAgeLimit: IAgeLimitDefault = Object.keys(this.ageLimitConfig).reduce(
            (ageAcc: IAgeLimitDefault, key: string): IAgeLimitDefault => {
                const formatedKey: string = _upperFirst(key);
                return key !== "journeyDate" ? {
                    ...ageAcc,
                    [formatedKey]: {
                        min: moment().subtract(this.ageLimitConfig[key].age.max, 'years').toDate(),
                        max: moment().subtract(this.ageLimitConfig[key].age.min, 'years').toDate()
                    }
                } : ageAcc;
            }, defaultAcc
        );
        this.ageLimitDefaults = parseAgeLimit;
    }

    public emitData(): void {
        this.formBuilderService.emitInfo('traveller');
    }

    public blurField(config: IFieldSpecs): void {
        const control: AbstractControl = config.formGroup.get(config.formControlName);

        control.markAsDirty({ onlySelf: true });
        control.markAsTouched({ onlySelf: true });
        control.updateValueAndValidity();

        this.emitData();
    }

    public blurPassportField(data: [FormGroup,IFieldSpecs]): void {
        const travellerGroup: FormGroup = data[0];
        const config: IFieldSpecs = data[1];
        PASSPORT_FIELDS.forEach((fieldName: string) => {
            const control: AbstractControl = travellerGroup.get(fieldName);
            control.updateValueAndValidity();
        });

        this.blurField(config);
    }

    /**
     * Date range for the passport validity.end
     */

    public getBirthRange(index: number): string {
        return this.yearRange[index];
    }

    public getName(index: number): string {

        const traveller = this.group.get(_toString(index));
        const firstNameValue = traveller.get('firstName').value;
        const lastNameValue = traveller.get('lastName').value;
        if (!lastNameValue && !firstNameValue) {
            return null;
        } else {
            return lastNameValue + " " + firstNameValue;
        }
    }

    public setOpenApis(index: number) {
        this.initIdentityInfo();
        _set(this.openApis, index, !this.openApis[index]);
    }
    public setApisSate(index: number, state: boolean):void {
        this.openApis[index] = state;
    }

    public getOpenApis(index: number) {
        return this.openApis[index];
    }

    /**
     * set all last name after the first pax first name is typed
     */
    public setLastName(_event: Event): void {
        const first = this.group.get('0').value;

        //for each pax except first, if equal or null, update
        for (let i = 1; i < this.group.value.length; i++) {
            if (_find(this.sameAndEmptyLastNameTraveller, (o) => {
                return o === i;
            })) {
                this.group.get(_toString(i)).patchValue({ 'lastName': first.lastName });
            }
        }
    }

    public testLastNames(): void {

        //reinit array at each focus
        this.sameAndEmptyLastNameTraveller = [];

        //get first pax infos
        const first = this.group.get('0').value;

        //for each other pax, push index in array if empty or equal
        for (let i = 1; i < this.group.value.length; i++) {
            const other = this.group.get(_toString(i)).value;
            if (!other.lastName) {
                this.sameAndEmptyLastNameTraveller.push(i);
            } else if (other.lastName === first.lastName) {
                this.sameAndEmptyLastNameTraveller.push(i);
            }
        }
    }

    public getTextFieldConfig(formGroup: FormGroup, formControlName: string, extraValidators?: IValidator[]): IFieldSpecs {
        const requiredFields: string[] = ['title', 'type', 'firstName', 'lastName'];
        const validatorField: { validators: IValidator[] } = { validators: [] };
        if (requiredFields.find((field: string) => field === formControlName)) {
            validatorField.validators = [new Required()];
        }

        if (extraValidators) {
            validatorField.validators.push(...extraValidators);
        }

        return {
            formGroup,
            formControlName,
            label: WORDING[formControlName],
            ...validatorField
        };
    }

    public onParentFormCheck(): void {
        this.identityInfoEditorComponents.forEach((item: IdentityInfoEditorComponent) => item.onParentFormCheck());
    }

    /**
     * Date range for the passport validity.end
     */
    protected getPassportDateRange(): { min: number, max: number } {
        const startYear: string = moment().subtract('10', 'years').format('YYYY');
        const endYear: string = moment().add('10', 'years').format('YYYY');

        return {
            min: +startYear,
            max: +endYear
        };
    }

    protected getPassportValidityConfig(travellerGroup: FormGroup, identityType: 'passport'|'identityCard', validators?: IValidator[]): IDateFieldSpecs {

        if (identityType === 'passport') {
            return {
                icon: 'fa fa-calendar',
                formControlName: 'passEnd',
                formGroup: travellerGroup.get([identityType, 'validity']) as FormGroup,
                label: Yii.t('wui-shared-js', "Date d'expiration"),
                yearRange: this.getPassportDateRange(),
                validators
            };
        }
        if (identityType === 'identityCard') {
            return {
                icon: 'fa fa-calendar',
                formControlName: 'cardEnd',
                formGroup: travellerGroup.get([identityType, 'validity']) as FormGroup,
                label: Yii.t('wui-shared-js', "Date d'expiration"),
                yearRange: this.getPassportDateRange(),
                validators
            };
        }
        throw new RuntimeError(`Unknown identity document ${identityType}`);
    }

    private getDateFieldConfig(
        formGroup: FormGroup,
        formControlName: string,
        label: string,
        yearRange: { min: number; max: number },
        validators?: IValidator[],
    ): IDateFieldSpecs {
            return {
                icon: 'fa fa-calendar',
                formControlName,
                formGroup,
                label,
                yearRange,
                validators
            };
    }

    protected initFields(): void {
        this.isTravellerReadonly = this.travellerItem.map((traveller: ITravellerObject) => traveller.isReadonly || false || this.contextIsModifySegment);
        this.fieldsConfig = this.group.controls.map((group: FormGroup, index: number) => this.initTravellerFields(group, index));
        if(this.travellerCollapse.link === 1){
            this.travellerItem
                .forEach(
                    (traveller: ITravellerObject, index: number): void => this.setApisSate(index, this.travellerCollapse.open === 1)
                );
        }

    }

    protected initTravellerFields(group: FormGroup, index: number): { [key: string]: IFieldSpecs } {
        const requiredIfPassportNotEmpty: RequiredIf = new RequiredIf(
            (): boolean => PASSPORT_FIELDS.some((field: string) => group.get(field).value)
        );

        const requiredIfIdentityCardNotEmpty: RequiredIf = new RequiredIf(
            (): boolean => IDENTITY_CARD_FIELDS.some((field: string) => group.get(field).value)
        );

        const requiredIfSfpdNotEmpty: RequiredIf = new RequiredIf(
            (): boolean => SFPD_FIELDS.some((field: string) => group.get(field).value)
        );

        return {
            title: {
                label: this.wording.title,
                formGroup: group,
                formControlName: "title",
                validators: [new Required()]
            },
            type: {
                label: this.wording.type,
                formGroup: group,
                formControlName: "type",
                validators: [new Required()]
            },
            lastName: this.getTextFieldConfig(group, 'lastName'),
            firstName: this.getTextFieldConfig(group, 'firstName'),
            gender : {
                label: this.wording.gender,
                formGroup: group,
                formControlName: "xGender"
            },
            birthDate: this.getBirthDateConfig(group, 'birthDate', index),
            citizenshipPassport: this.getTextFieldConfig(group, 'passportCitizenship', [requiredIfPassportNotEmpty]),
            citizenshipIdentityCard: this.getTextFieldConfig(group, 'identityCardCitizenship', [requiredIfIdentityCardNotEmpty]),

            passportNumber: this.getTextFieldConfig(group.get('passport') as FormGroup, 'passNumber', [requiredIfPassportNotEmpty]),
            passportIssuance: this.getTextFieldConfig(group.get(['passport', 'issuance']) as FormGroup, 'passCountry', [requiredIfPassportNotEmpty]),
            passportValidity: this.getPassportValidityConfig(group, 'passport',[requiredIfPassportNotEmpty]),

            identityCardNumber: this.getTextFieldConfig(group.get('identityCard') as FormGroup, 'cardNumber', [requiredIfIdentityCardNotEmpty]),
            identityCardIssuance: this.getTextFieldConfig(
                group.get(['identityCard', 'issuance']) as FormGroup, 'cardCountry', [requiredIfIdentityCardNotEmpty]),
            identityCardValidity: this.getPassportValidityConfig(group, 'identityCard',[requiredIfIdentityCardNotEmpty]),
        };
    }
    public updateDateConfig(): void {
        this.group.controls.forEach((travellerGroup: FormGroup, index: number) => {
            this.fieldsConfig[index].birthDate = this.getBirthDateConfig(travellerGroup, 'birthDate', index);
            if (this.travellerItem[index] && this.travellerItem[index].birthDate && typeof this.travellerItem[index].birthDate === "string" && this.travellerItem[index].birthDate.indexOf('-') > 0) {
                _set(this.travellerItem[index], ['birthDate'], moment(this.travellerItem[index].birthDate).toDate());
            }

            if (this.travellerItem[index] && this.travellerItem[index].birthDate) {
                travellerGroup.get('birthDate').setValue(this.travellerItem[index].birthDate);
            }
        });
    }

    private updateNameConfig(): void {
        this.group.controls.forEach((travellerGroup: FormGroup, index: number): void => {
            if(this.travellerItem && this.travellerItem[index] && this.fieldsConfig && this.fieldsConfig[index]){
                this.fieldsConfig[index].lastName.disabled = this.travellerItem[index].isReadonly;
                this.fieldsConfig[index].firstName.disabled = this.travellerItem[index].isReadonly;
            }
        });
    }

    private updateBirthDateConfig(): void {
        this.isTravellerReadonly.forEach((travellerReadonly: boolean, index: number) => {
            if (travellerReadonly) {
                this.fieldsConfig[index].type.disabled = true;
                this.fieldsConfig[index].birthDate.disabled = true;
            }
        });
    }

    /**
     * get config for birthDate field
     */
    protected getBirthDateConfig(travellerGroup: FormGroup, formControlName: string, index: number): IDateFieldSpecs {
        const travellerType: AbstractControl = travellerGroup.get(['type']);
        const travellerAge: string = travellerGroup.get(['age']).value.toString();
        const validators: IValidator[] = [
            new IsPast(),
            new RequiredIf((): boolean => {
                const required: boolean = Boolean(this.apiServiceQualityMessage?.length);
                if(required){
                    return true;
                }
                return travellerType.value !== 'Adult';
            }),
        ];
        if (travellerAge === "") {
            validators.push(new IsCorrectDateForType(this.ageLimitDefaults, travellerType));
        }
        return {
            formGroup: travellerGroup,
            formControlName,
            icon: 'fa fa-calendar',
            label: Yii.t('wui-shared-js', 'Date de naissance'),
            minDate: this.ageLimitDefaults[travellerGroup.value.type].min,
            maxDate: this.ageLimitDefaults[travellerGroup.value.type].max,
            defaultDate: this.defaultBirthDate[travellerGroup.value.type],
            forceYearInPast: true,
            validators,
        };
    }

    private initIdentityInfo(): void {
        // Passport
        this.initialPassport = this.travellerItem.map((traveller: ITravellerObject): IIdentityInfo => {
            if (traveller.passport) {
                return {
                    holder: this.travellerFieldsetService.buildHolder(traveller),
                    number: (traveller.passport.number || traveller.passport.passNumber),
                    validity: traveller.passport.validity ?
                        {
                        end:  traveller.passport.validity.end || traveller.passport.validity.passEnd,
                    } : null,
                    placeOfIssue: traveller.passport.issuance ?
                        {
                            country: traveller.passport.issuance.country || traveller.passport.issuance.passCountry
                        }: null,
                };
            }
            return this.travellerFieldsetService.emptyIdentityInfo(traveller);
        });

        // IdentityCard
        this.initialIdentityCard = this.travellerItem.map((traveller: ITravellerObject): IIdentityInfo => {
            if (traveller.identityCard) {
                return {
                    holder: this.travellerFieldsetService.buildHolder(traveller),
                    number: (traveller.identityCard.number || traveller.identityCard.cardNumber),
                    validity: traveller.identityCard.validity ?
                        {
                            end:  traveller.identityCard.validity.end || traveller.identityCard.validity.cardEnd,
                        } : null,
                    placeOfIssue: traveller.identityCard.issuance ?
                        {
                            country: traveller.identityCard.issuance.country || traveller.identityCard.issuance.cardCountry,
                        }: null,
                };
            }
            return this.travellerFieldsetService.emptyIdentityInfo(traveller);
        });
    }

    private setWording(key: string, value: string): void {
        if (this.wording[key]) {
            this.wording[key] = value;
        }
    }
}
